<template>
    <SelectRoot
        v-model="model"
        v-model:open="open"
        :name="name"
        :disabled="disabled"
        :required="required"
    >
        <SelectTrigger
            :class="[classes, $attrs.class]"
            :style="$attrs.style"
            v-bind="{ ...controlAttrs, ...fallthroughAttrs }"
        >
            <SelectValue
                as="span"
                :class="element('value')"
                :placeholder="placeholder"
            >
                <template
                    v-if="$slots.value"
                    #default="valueProps"
                >
                    <slot
                        name="value"
                        :selected-label="valueProps.selectedLabel"
                        :model-value="valueProps.modelValue"
                        :placeholder="placeholder"
                    />
                </template>
            </SelectValue>

            <SelectIcon
                as="span"
                :class="element('icon')"
                aria-hidden="true"
            >
                <slot name="icon">
                    &#9662;
                </slot>
            </SelectIcon>
        </SelectTrigger>

        <SelectPortal v-bind="portal">
            <SelectContent
                as="div"
                :class="[contentClasses, $attrs['content-class']]"
                :position="position"
                :side="side"
                :side-offset="sideOffset"
            >
                <SelectScrollUpButton
                    :class="cElement('scroll', cModifier('up'))"
                    aria-hidden="true"
                >
                    &#9650;
                </SelectScrollUpButton>

                <SelectViewport :class="cElement('viewport')">
                    <!-- The items mount in a detached fragment while the popup is closed.
                        Never gate this on `open`: SelectValue, BubbleSelect and typeahead
                        on the closed trigger all read the registry the mounted items fill. -->
                    <slot
                        v-if="$slots.default"
                        :model-value="model"
                        :open="open"
                    />
                    <template v-else>
                        <template
                            v-for="(entry, index) in entryRows"
                            :key="index"
                        >
                            <KSelectGroup
                                v-if="entry.type === 'group'"
                                :label="entry.label"
                            >
                                <KSelectItem
                                    v-for="(option, optionIndex) in entry.options"
                                    :key="optionIndex"
                                    :value="option.value"
                                    :disabled="option.disabled"
                                >
                                    {{ option.label }}
                                </KSelectItem>
                            </KSelectGroup>
                            <KSelectItem
                                v-else
                                :value="entry.option.value"
                                :disabled="entry.option.disabled"
                            >
                                {{ entry.option.label }}
                            </KSelectItem>
                        </template>
                    </template>
                </SelectViewport>

                <SelectScrollDownButton
                    :class="cElement('scroll', cModifier('down'))"
                    aria-hidden="true"
                >
                    &#9660;
                </SelectScrollDownButton>
            </SelectContent>
        </SelectPortal>
    </SelectRoot>
</template>

<script setup lang="ts">
import type { KSelectOptionGroup, KSelectProps } from './types'
import useBem from '@yeeroen/use-bem'
import {
    SelectContent,
    SelectIcon,
    SelectPortal,
    SelectRoot,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectTrigger,
    SelectValue,
    SelectViewport,
} from 'reka-ui'
import { computed, useAttrs } from 'vue'

import { useFieldControl } from '../field/use-field-control'
import KSelectGroup from './k-select-group.vue'
import KSelectItem from './k-select-item.vue'

defineOptions({ name: 'KSelect', inheritAttrs: false })

const props = withDefaults(defineProps<KSelectProps>(), {
    fullWidth: true,
    position: 'popper',
    side: 'bottom',
    sideOffset: 4,
    portalTo: undefined,
    // Left undefined on purpose: `useFieldControl` falls back to the KField context with
    // `??`, and a `false` default would win over an enclosing disabled field.
    as: undefined,
    disabled: undefined,
    required: undefined,
    invalid: undefined,
    size: undefined,
})

defineSlots<{
    /**
     * The items. Replaces `options` entirely — the escape hatch for icons, two-line
     * items or anything the option shape cannot express.
     */
    default?: (props: { modelValue: string | number | null, open: boolean }) => unknown
    /** Replaces the trigger's rendered value. */
    value?: (props: { selectedLabel: string[], modelValue: unknown, placeholder?: string }) => unknown
    /** Replaces the trigger's chevron. */
    icon?: () => unknown
}>()

const model = defineModel<string | number | null>({ default: null })
const open = defineModel<boolean>('open', { default: false })

const attrs = useAttrs()
const { block, element, modifier } = useBem('k-select')
// The popup lives in a portal, so it is a second BEM block, not `.k-select__content` —
// an element class would promise a DOM parent that is not there.
const { block: cBlock, element: cElement, modifier: cModifier } = useBem('k-select-content')

// `inheritAttrs: false` sweeps `class` and `style` into `$attrs`, but a caller adding a
// class means the trigger, and the field's `id` / aria hooks must land there too —
// without this the KField label would point at the renderless root.
const fallthroughAttrs = computed(() => {
    const { class: _class, style: _style, 'content-class': _contentClass, ...rest } = attrs

    return rest
})

const { controlAttrs, invalid, disabled, size } = useFieldControl(props)

const portal = computed(() => props.portalTo === false
    ? { disabled: true }
    : props.portalTo === undefined
        ? {}
        : { to: props.portalTo })

interface NormalizedOption {
    label: string
    value: string | number
    disabled?: boolean
}

type EntryRow
    = | { type: 'option', option: NormalizedOption }
        | { type: 'group', label: string, options: NormalizedOption[] }

const entryRows = computed<EntryRow[]>(() =>
    (props.options ?? []).map((entry): EntryRow => {
        if (isGroup(entry)) {
            return { type: 'group', label: entry.label, options: entry.options.map(normalizeOption) }
        }

        return { type: 'option', option: normalizeOption(entry) }
    }))

function isGroup(entry: unknown): entry is KSelectOptionGroup {
    return typeof entry === 'object'
        && entry !== null
        && 'options' in entry
        && Array.isArray((entry as KSelectOptionGroup).options)
}

function normalizeOption(option: NonNullable<KSelectProps['options']>[number]): NormalizedOption {
    if (typeof option === 'object' && option !== null && !isGroup(option)) {
        return { label: option.label, value: option.value, disabled: option.disabled }
    }

    const value = option as string | number

    return { label: String(value), value }
}

const classes = computed(() => block(
    modifier(size.value),
    modifier('invalid', invalid.value),
    modifier('disabled', disabled.value),
    modifier('full-width', props.fullWidth),
))

const contentClasses = computed(() => cBlock(
    cModifier(size.value),
))
</script>

<style lang="scss">
@use './k-select.scss';
</style>
